import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Coins, Mail, ShieldCheck, Smartphone, Sparkles } from 'lucide-react';
import { Logo } from '../components/Logo';
import { PhoneMockup } from '../components/PhoneMockup';

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: { opacity: 1, y: 0 },
};

/**
 * The public face of Drameeka — the only route in this app a signed-out
 * visitor sees other than the login form itself (App.tsx). Two audiences:
 * prospective creators (the primary reason this exists right now — the
 * platform is on hold for the Play Store while creator deals get signed) and
 * general visitors. No invented numbers or testimonials — the phone mockup is
 * a real emulator screenshot of THE RED HEEL, the only thing actually in the
 * catalog today, and every other claim is a real product mechanic.
 */
export function LandingPage() {
  return (
    <div className="min-h-full bg-canvas text-ink overflow-x-clip">
      <header className="sticky top-0 z-20 backdrop-blur-md bg-canvas/75 border-b border-border/60">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Logo size={26} />
            <span className="text-base font-bold tracking-tight text-white">Drameeka</span>
          </div>
          <Link
            to="/login"
            className="text-sm font-medium text-ink-muted hover:text-white transition-colors"
          >
            Staff &amp; creator sign in
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-52 -left-20 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(circle, var(--color-primary), transparent 70%)' }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute top-10 -right-32 w-[520px] h-[520px] rounded-full opacity-[0.14] blur-3xl"
          style={{ background: 'radial-gradient(circle, var(--color-accent), transparent 70%)' }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        <div className="relative max-w-6xl mx-auto px-6 pt-14 sm:pt-20 pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial="hidden"
            animate="show"
            variants={fadeUp}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            className="text-center lg:text-left"
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-primary-light bg-primary/10 border border-primary/25 rounded-full px-3 py-1">
              <Sparkles className="w-3.5 h-3.5" />
              Now signing creator partners
            </span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-[3.4rem] font-extrabold tracking-tight text-white leading-[1.08]">
              Short drama,
              <br />
              built for a phone.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-ink-muted leading-relaxed max-w-lg mx-auto lg:mx-0">
              Drameeka is a vertical, episode-by-episode drama app. Viewers unlock episodes with
              coins; creators bring the series. We're building the catalog now, one real
              partnership at a time.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center lg:justify-start gap-3">
              <a
                href="#creators"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-semibold transition-colors"
              >
                Partner with us
                <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-border hover:border-ink-muted text-ink text-sm font-semibold transition-colors"
              >
                Staff sign in
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 28, rotate: -2 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut', delay: 0.15 }}
          >
            <PhoneMockup src="/hero-screenshot.png" />
          </motion.div>
        </div>
      </section>

      {/* What is Drameeka */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: '-80px' }}
        variants={{ show: { transition: { staggerChildren: 0.1 } } }}
        className="max-w-6xl mx-auto px-6 py-16 border-t border-border"
      >
        <div className="grid sm:grid-cols-3 gap-8">
          {[
            {
              icon: Smartphone,
              title: 'Mobile-first',
              body: 'A vertical, swipe-through episode feed built for Android from the ground up, not a desktop site squeezed onto a phone.',
            },
            {
              icon: Coins,
              title: 'Coins, not subscriptions',
              body: 'The first episode of every series is free. Viewers unlock the rest with coins, earned by watching ads or bought directly. No forced monthly commitment.',
            },
            {
              icon: ShieldCheck,
              title: 'Reviewed, not automated',
              body: 'Every series a creator submits is reviewed by our team before it goes live. The catalog is small on purpose, for now.',
            },
          ].map(({ icon: Icon, title, body }) => (
            <motion.div
              key={title}
              variants={fadeUp}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            >
              <Icon className="w-5 h-5 text-accent" />
              <h3 className="mt-3 text-sm font-semibold text-white">{title}</h3>
              <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{body}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* For creators */}
      <section id="creators" className="relative border-t border-border bg-surface/40 scroll-mt-6 overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute bottom-0 right-0 w-[480px] h-[480px] rounded-full opacity-[0.1] blur-3xl translate-x-1/3 translate-y-1/3"
          style={{ background: 'radial-gradient(circle, var(--color-primary), transparent 70%)' }}
        />
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: '-80px' }}
          variants={{ show: { transition: { staggerChildren: 0.08 } } }}
          className="relative max-w-6xl mx-auto px-6 py-16 sm:py-20"
        >
          <motion.div variants={fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">
              Bring your series to Drameeka
            </h2>
            <p className="mt-4 text-sm sm:text-base text-ink-muted leading-relaxed">
              We're deliberately holding off on a public launch until there's a real catalog worth
              opening the doors to. If you write, shoot or own the rights to short-form drama, this
              is the ground floor: a revenue share on every coin spent unlocking your episodes, and
              direct say in what the reviewed catalog looks like.
            </p>
          </motion.div>

          <div className="mt-12 relative grid sm:grid-cols-3 gap-10">
            <div
              aria-hidden
              className="hidden sm:block absolute top-3.5 left-[16.5%] right-[16.5%] h-px bg-gradient-to-r from-primary/40 via-primary/15 to-transparent"
            />
            {[
              {
                n: 1,
                title: 'Get in touch',
                body: 'Send us what you have: a finished series, a pilot, or just an idea worth talking through.',
              },
              {
                n: 2,
                title: 'We review it together',
                body: "Your own Creator Studio login lets you upload episodes, set pricing and track what's live. We review before anything reaches viewers.",
              },
              {
                n: 3,
                title: 'You earn a share',
                body: 'A revenue share on the coins viewers spend unlocking your episodes. Built into the platform, not a spreadsheet promise.',
              },
            ].map(({ n, title, body }) => (
              <motion.div key={n} variants={fadeUp} transition={{ duration: 0.5 }} className="relative">
                <span className="relative z-10 flex items-center justify-center w-7 h-7 rounded-full bg-primary text-white text-xs font-bold ring-4 ring-surface/40">
                  {n}
                </span>
                <h3 className="mt-3 text-sm font-semibold text-white">{title}</h3>
                <p className="mt-1.5 text-sm text-ink-muted leading-relaxed">{body}</p>
              </motion.div>
            ))}
          </div>

          <motion.a
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            href="mailto:felixawere01@gmail.com?subject=Bringing%20a%20series%20to%20Drameeka"
            className="mt-12 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary hover:bg-primary-dark text-white text-sm font-semibold transition-colors"
          >
            <Mail className="w-4 h-4" />
            Get in touch
          </motion.a>
        </motion.div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 flex items-center justify-between text-xs text-ink-muted">
        <div className="flex items-center gap-2">
          <Logo size={16} />
          <span>Drameeka</span>
        </div>
        <Link to="/login" className="hover:text-white transition-colors">
          Staff &amp; creator sign in
        </Link>
      </footer>
    </div>
  );
}
